import React, { useState, useEffect } from 'react';
import { X } from 'lucide-react';
import { usePackages } from '../hooks/usePackages';
import { TrainingPackage, TrainingPackageUpdate } from '../types';

interface EditPackageModalProps {
  isOpen: boolean;
  onClose: () => void;
  package: TrainingPackage | null;
}

interface PackageFormErrors {
  name?: string;
  sessions_count?: string;
  price?: string;
  description?: string;
  is_active?: string;
}

const EditPackageModal: React.FC<EditPackageModalProps> = ({ isOpen, onClose, package: pkg }) => {
  const { updatePackage, isUpdateLoading } = usePackages();
  
  const [formData, setFormData] = useState<TrainingPackageUpdate>({
    name: '',
    sessions_count: 8,
    price: 0,
    description: '',
    is_active: true
  });

  const [errors, setErrors] = useState<PackageFormErrors>({});

  // Заполняем форму данными пакета при открытии
  useEffect(() => {
    if (pkg && isOpen) {
      setFormData({
        name: pkg.name,
        sessions_count: pkg.sessions_count,
        price: pkg.price,
        description: pkg.description || '',
        is_active: pkg.is_active
      });
      setErrors({});
    }
  }, [pkg, isOpen]);

  const handleChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
    const { name, value, type } = e.target;
    let processedValue: any = value;
    
    if (type === 'checkbox') {
      processedValue = (e.target as HTMLInputElement).checked;
    } else if (name === 'sessions_count' || name === 'price') {
      processedValue = Number(value);
    }
    
    setFormData(prev => ({
      ...prev,
      [name]: processedValue
    }));
    
    // Очищаем ошибку поля при изменении
    if (errors[name as keyof PackageFormErrors]) {
      setErrors(prev => ({ ...prev, [name]: undefined }));
    }
  };

  const validateForm = (): boolean => {
    const newErrors: PackageFormErrors = {};

    if (!formData.name?.trim()) {
      newErrors.name = 'Название обязательно';
    }

    if (formData.sessions_count !== undefined && formData.sessions_count <= 0) {
      newErrors.sessions_count = 'Количество тренировок должно быть больше 0';
    }

    if (formData.price !== undefined && formData.price < 0) {
      newErrors.price = 'Цена не может быть отрицательной';
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    
    if (!pkg || !validateForm()) {
      return;
    }

    try {
      const updateData = {
        ...formData,
        description: formData.description?.trim() || undefined
      };
      
      updatePackage({ packageId: pkg.id, data: updateData });
      onClose();
    } catch (error) {
      console.error('Error updating package:', error);
    }
  };

  const handleClose = () => {
    setErrors({});
    onClose();
  };

  if (!isOpen || !pkg) return null;

  return (
    <div className="fixed inset-0 z-50 overflow-y-auto">
      <div className="flex items-end justify-center min-h-screen pt-4 px-4 pb-20 text-center sm:block sm:p-0">
        <div className="fixed inset-0 transition-opacity" onClick={handleClose}>
          <div className="absolute inset-0 bg-gray-500 opacity-75"></div>
        </div>

        <span className="hidden sm:inline-block sm:align-middle sm:h-screen">&#8203;</span>

        <div className="inline-block align-bottom bg-white rounded-lg text-left overflow-hidden shadow-xl transform transition-all sm:my-8 sm:align-middle sm:max-w-lg sm:w-full">
          <div className="bg-white px-4 pt-5 pb-4 sm:p-6 sm:pb-4">
            <div className="flex items-center justify-between mb-4">
              <h3 className="text-lg font-medium text-gray-900">
                Редактировать пакет тренировок
              </h3>
              <button
                onClick={handleClose}
                className="text-gray-400 hover:text-gray-600"
              >
                <X className="w-6 h-6" />
              </button>
            </div>

            <form onSubmit={handleSubmit} className="space-y-4">
              <div>
                <label htmlFor="name" className="block text-sm font-medium text-gray-700">
                  Название пакета *
                </label>
                <input
                  type="text"
                  id="name"
                  name="name"
                  value={formData.name}
                  onChange={handleChange}
                  className={`mt-1 block w-full px-3 py-2 border rounded-md shadow-sm focus:outline-none focus:ring-blue-500 focus:border-blue-500 sm:text-sm ${
                    errors.name ? 'border-red-300' : 'border-gray-300'
                  }`}
                  placeholder="Например: Базовый пакет"
                />
                {errors.name && (
                  <p className="mt-1 text-sm text-red-600">{errors.name}</p>
                )}
              </div>

              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label htmlFor="sessions_count" className="block text-sm font-medium text-gray-700">
                    Количество тренировок *
                  </label>
                  <input
                    type="number"
                    id="sessions_count"
                    name="sessions_count"
                    value={formData.sessions_count}
                    onChange={handleChange}
                    min="1"
                    className={`mt-1 block w-full px-3 py-2 border rounded-md shadow-sm focus:outline-none focus:ring-blue-500 focus:border-blue-500 sm:text-sm ${
                      errors.sessions_count ? 'border-red-300' : 'border-gray-300'
                    }`}
                  />
                  {errors.sessions_count && (
                    <p className="mt-1 text-sm text-red-600">{errors.sessions_count}</p>
                  )}
                </div>

                <div>
                  <label htmlFor="price" className="block text-sm font-medium text-gray-700">
                    Цена (₽) *
                  </label>
                  <input
                    type="number"
                    id="price"
                    name="price"
                    value={formData.price}
                    onChange={handleChange}
                    min="0"
                    step="0.01"
                    className={`mt-1 block w-full px-3 py-2 border rounded-md shadow-sm focus:outline-none focus:ring-blue-500 focus:border-blue-500 sm:text-sm ${
                      errors.price ? 'border-red-300' : 'border-gray-300'
                    }`}
                  />
                  {errors.price && (
                    <p className="mt-1 text-sm text-red-600">{errors.price}</p>
                  )}
                </div>
              </div>

              <div>
                <label htmlFor="description" className="block text-sm font-medium text-gray-700">
                  Описание
                </label>
                <textarea
                  id="description"
                  name="description"
                  value={formData.description}
                  onChange={handleChange}
                  rows={3}
                  className="mt-1 block w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-blue-500 focus:border-blue-500 sm:text-sm"
                  placeholder="Опишите что включает в себя пакет..."
                />
              </div>

              <div className="flex items-center">
                <input
                  id="is_active"
                  name="is_active"
                  type="checkbox"
                  checked={formData.is_active}
                  onChange={handleChange}
                  className="h-4 w-4 text-blue-600 focus:ring-blue-500 border-gray-300 rounded"
                />
                <label htmlFor="is_active" className="ml-2 block text-sm text-gray-900">
                  Пакет активен
                </label>
              </div>

              <div className="flex justify-end space-x-3 pt-4">
                <button
                  type="button"
                  onClick={handleClose}
                  className="px-4 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-md shadow-sm hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500"
                >
                  Отмена
                </button>
                <button
                  type="submit"
                  disabled={isUpdateLoading}
                  className="px-4 py-2 text-sm font-medium text-white bg-blue-600 border border-transparent rounded-md shadow-sm hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500 disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  {isUpdateLoading ? 'Сохранение...' : 'Сохранить'}
                </button>
              </div>
            </form>
          </div>
        </div>
      </div>
    </div>
  );
};

export default EditPackageModal;